/**
 * The mobile navigation drawer — DESIGN.md §13.1, §13.6.
 *
 * A Radix Dialog rather than a translated `<nav>`. The old drawer was always in
 * the DOM and merely slid off-screen, which left twelve invisible tab stops
 * ending in Sign out, and Esc did nothing (SHELL-7, SHELL-8). A Dialog gives
 * the focus trap, Esc, the scrim, and — because it is only mounted while open —
 * a closed drawer that contributes nothing to the tab order at all.
 */
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SidebarNav } from '@/components/shell/sidebar-nav';

export function MobileDrawer({
  open,
  onOpenChange,
  returnFocusTo,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The hamburger. Radix restores focus to its own `Trigger`, and this
   *  drawer has none — its open state is owned by the shell — so without
   *  this, closing dropped focus to `<body>` (KEYS-2). */
  returnFocusTo?: React.RefObject<HTMLElement>;
}): JSX.Element {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50" />
        <DialogPrimitive.Content
          className="bg-card data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left fixed inset-y-0 left-0 z-50 flex w-[17rem] max-w-[85vw] flex-col border-r shadow-xl duration-200"
          aria-label="Navigation"
          onCloseAutoFocus={(event) => {
            const target = returnFocusTo?.current;
            if (!target) return;
            event.preventDefault();
            target.focus();
          }}
        >
          <DialogPrimitive.Title className="sr-only">Navigation</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Move between your tasks, inbox, projects and reports.
          </DialogPrimitive.Description>

          <DialogPrimitive.Close asChild>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2 z-10"
              aria-label="Close navigation"
            >
              <X />
            </Button>
          </DialogPrimitive.Close>

          {/* The drawer is never collapsed: a rail inside a drawer is a rail
              you cannot read, and the screen it covers is already narrow. */}
          <SidebarNav collapsed={false} showTimer={false} onNavigate={() => onOpenChange(false)} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
